import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { Menu } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import BookingPage from './pages/BookingPage';
import ResourcesPage from './pages/ResourcesPage';
import AdminPage from './pages/AdminPage';
import SubjectsPage from './pages/SubjectsPage';
import SchedulingPage from './pages/SchedulingPage';
import AboutPage from './pages/AboutPage';
import ProfilePage from './pages/ProfilePage';
import PaymentsPage from './pages/PaymentsPage';
import ConsultationPage from './pages/ConsultationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import FeedbackPage from './pages/FeedbackPage';
import ChatPage from './pages/ChatPage';
import QuizzesPage from './pages/QuizzesPage';
import QuizPlayerPage from './pages/QuizPlayerPage';
import QuizResultsPage from './pages/QuizResultsPage';
import StudentsPage from './pages/StudentsPage';
import StudentDetailPage from './pages/StudentDetailPage';
import OxbridgePage from './pages/OxbridgePage';
import ContactPage from './pages/ContactPage';
import EmailChangeConfirmPage from './pages/EmailChangeConfirmPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Footer from './components/common/Footer';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { navigateToLogin } = useNavigation();
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h1>
          <p className="text-gray-600 mb-6">Please log in to access this page.</p>
          <button
            onClick={navigateToLogin}
            className="btn btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

const TutorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { navigateToLogin, navigateToDashboard } = useNavigation();
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h1>
          <p className="text-gray-600 mb-6">Please log in to access this page.</p>
          <button
            onClick={navigateToLogin}
            className="btn btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  if (user.role !== 'TUTOR') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Tutor Access Only</h1>
          <p className="text-gray-600 mb-6">This page is only accessible to tutors.</p>
          <button
            onClick={navigateToDashboard}
            className="btn btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme} min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900`}>
      {/* Fixed Sidebar Toggle Button - Aligned with fixed header */}
      {user && (
        <button
          onClick={toggleSidebar}
          className="fixed top-3 left-4 z-50 p-2 bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg shadow-md border border-gray-200 transition-colors"
          title="Toggle Sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      
      <Header />
      <div className="flex flex-1 pt-16">
        {user && <Sidebar />}
        {/* Mobile sidebar overlay - only show when sidebar is expanded on mobile */}
        {user && isMobile && !isCollapsed && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-10" 
            onClick={() => toggleSidebar()}
          />
        )}
        <main className={`flex-1 transition-all duration-300 ${
          user ? (isMobile ? 'ml-0' : (isCollapsed ? 'ml-16' : 'ml-64')) : ''
        } ${isMobile ? 'w-full max-w-full overflow-x-hidden' : ''}`}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/email-change/confirm" element={<EmailChangeConfirmPage />} />
            <Route path="/subjects" element={<SubjectsPage />} />
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute>
                  <DashboardPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <PrivateRoute>
                  <ProfilePage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/booking" 
              element={
                <PrivateRoute>
                  <BookingPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/schedule" 
              element={
                <PrivateRoute>
                  <SchedulingPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/resources" 
              element={
                <PrivateRoute>
                  <ResourcesPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/payments" 
              element={
                <PrivateRoute>
                  <PaymentsPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/consultation" 
              element={
                <PrivateRoute>
                  <ConsultationPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/feedback" 
              element={
                <PrivateRoute>
                  <FeedbackPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/chat" 
              element={
                <PrivateRoute>
                  <ChatPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/students" 
              element={
                <TutorRoute>
                  <StudentsPage />
                </TutorRoute>
              } 
            />
            <Route 
              path="/students/:studentId" 
              element={
                <TutorRoute>
                  <StudentDetailPage />
                </TutorRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <TutorRoute>
                  <AdminPage />
                </TutorRoute>
              } 
            />
            <Route 
              path="/quizzes" 
              element={
                <PrivateRoute>
                  <QuizzesPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/quiz/:quizId" 
              element={
                <PrivateRoute>
                  <QuizPlayerPage />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/quiz-results/:attemptId" 
              element={
                <PrivateRoute>
                  <QuizResultsPage />
                </PrivateRoute>
              } 
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/oxbridge" element={<OxbridgePage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* Legacy public routes — redirect old bookmarks into the new IA. */}
            <Route path="/rates" element={<Navigate to="/subjects" replace />} />
            <Route path="/university" element={<Navigate to="/oxbridge" replace />} />
            <Route path="/for-parents" element={<Navigate to="/about" replace />} />
          </Routes>
        </main>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <NavigationProvider>
            <AuthProvider>
              <SidebarProvider>
                <ThemeProvider>
                  <AppContent />
                </ThemeProvider>
              </SidebarProvider>
            </AuthProvider>
          </NavigationProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
